import axios from '@/lib/axios';
import { OnboardingTask } from '@/types/models';

// API call optimization - prevent excessive calls
let isCheckingAssignments = false;
let checkTimeout: NodeJS.Timeout | null = null;
let lastCheckTime = 0;
const MIN_INTERVAL = 20000; // 20 seconds minimum between checks

// Debounced function to prevent multiple simultaneous checks
const debouncedCheckAndFix = async () => {
  if (isCheckingAssignments) {
    return;
  }

  // Check if enough time has passed since last check
  const now = Date.now();
  if (now - lastCheckTime < MIN_INTERVAL) {
    return;
  }

  isCheckingAssignments = true;
  lastCheckTime = now;

  try {
    await checkAndFixTaskAssignments();
  } catch (error) {
    // Silent error handling
  } finally {
    isCheckingAssignments = false;
  }
};

// Track operation types to make intelligent decisions
let recentOperations: Array<{ type: string, timestamp: number }> = [];

// Function to determine if a check is really needed
const shouldTriggerCheck = (operationType: string): boolean => {
  const now = Date.now();

  // Clean old operations (older than 1 minute)
  recentOperations = recentOperations.filter(op => now - op.timestamp < 60000);

  // Add current operation
  recentOperations.push({ type: operationType, timestamp: now });

  // High priority operations always trigger checks
  const highPriorityOps = ['create', 'delete'];
  if (highPriorityOps.includes(operationType)) {
    return true;
  }

  // For status changes, only trigger if there were multiple changes
  if (operationType === 'status_change') {
    const statusChanges = recentOperations.filter(op => op.type === 'status_change');
    if (statusChanges.length >= 3) {
      return true;
    } else {
      return false;
    }
  }

  // For updates, only trigger if there were multiple updates
  if (operationType === 'update') {
    const updates = recentOperations.filter(op => op.type === 'update');
    if (updates.length >= 2) {
      return true;
    } else {
      return false;
    }
  }

  // For fetches, only trigger if there were multiple fetches
  if (operationType === 'fetch') {
    const fetches = recentOperations.filter(op => op.type === 'fetch');
    if (fetches.length >= 5) {
      return true;
    } else {
      return false;
    }
  }

  return false;
};

// Enhanced batch check function
const batchCheckAndFix = (operationType: string = 'unknown') => {
  // Check if this operation should trigger a check
  if (!shouldTriggerCheck(operationType)) {
    return;
  }

  if (checkTimeout) {
    clearTimeout(checkTimeout);
  }

  checkTimeout = setTimeout(() => {
    debouncedCheckAndFix();
  }, 1000); // 3 seconds delay
};

// Additional service-specific types
export interface OnboardingTaskCreate {
  title: string;
  description?: string;
  category?: string;
  dueDate?: string;
  [key: string]: any;
}

export interface OnboardingTaskUpdate {
  title?: string;
  description?: string;
  category?: string;
  dueDate?: string;
  status?: 'pending' | 'in_progress' | 'completed';
  weight?: number;
  [key: string]: any;
}

const processOnboardingTask = (task: any): OnboardingTask => {
  return {
    ...task,
    _id: task._id || task.id,
    id: task.id || task._id, // Keep id for backward compatibility
  };
};

// Get all onboarding tasks (with optional params for pagination/filters)
export async function getOnboardingTasks(params?: Record<string, any>) {
  const res = await axios.get('/onboarding-tasks', { params });

  let tasks = [];

  // Process each task in the response if the data is an array
  if (res.data && Array.isArray(res.data.data)) {
    tasks = res.data.data.map(processOnboardingTask);
  } else if (Array.isArray(res.data)) {
    tasks = res.data.map(processOnboardingTask);
  } else if (res.data && typeof res.data === 'object') {
    // If it's a single object, wrap it or check for common properties
    tasks = Array.isArray(res.data) ? res.data : [];
  }

  // Use optimized batch checking instead of direct API calls
  batchCheckAndFix('fetch');

  return tasks;
}

// Create a new onboarding task
export async function createOnboardingTask(data: OnboardingTaskCreate) {
  try {
    const res = await axios.post('/onboarding-tasks', data);

    const newTask = processOnboardingTask(res.data);

    // Ensure we have a valid MongoDB ObjectId
    const taskIdToUse = newTask._id || newTask.id;
    if (!taskIdToUse) {
      throw new Error('No valid task ID found after creation');
    }

    // Automatically run check and fix after task creation with a longer delay
    setTimeout(() => {
      batchCheckAndFix('create');
    }, 1000); // 3 second delay before attempting assignment

    // Return the task without immediate assignment
    return {
      ...newTask,
      assignmentSuccess: false,
      assignmentResults: [],
      message: 'Task created successfully - assignment will be processed automatically'
    };
  } catch (error) {
    throw error;
  }
}

// Update an onboarding task
export async function updateOnboardingTask(id: string, data: OnboardingTaskUpdate) {
  try {
    const res = await axios.patch(`/onboarding-tasks/${id}`, data);

    const updatedTask = processOnboardingTask(res.data);

    // Automatically recalculate completion for all onboardings if status was changed
    if (data.status) {
      try {
        const { calculateCompletionForAllOnboardings } = await import('./onboarding.service');
        await calculateCompletionForAllOnboardings();
      } catch (error) {
        // Silent error handling
      }
    }

    // Automatically check and fix task assignments after updating
    batchCheckAndFix('update');

    return updatedTask;
  } catch (error) {
    throw error;
  }
}

// Delete (soft delete) an onboarding task
export async function deleteOnboardingTask(id: string) {
  try {
    const res = await axios.delete(`/onboarding-tasks/${id}`);

    // Automatically check and fix task assignments after deletion
    batchCheckAndFix('delete');

    return res.data;
  } catch (error) {
    throw error;
  }
}

// Function to check and fix task assignments for all onboarding records
export async function checkAndFixTaskAssignments() {
  try {
    // Get all onboarding tasks
    const tasksResponse = await axios.get('/onboarding-tasks');
    const allTasks = tasksResponse.data?.data || tasksResponse.data || [];

    if (allTasks.length === 0) {
      return { success: false, message: 'No tasks found to assign' };
    }

    // Get all onboarding records
    const onboardingsResponse = await axios.get('/onboarding');
    const onboardings = onboardingsResponse.data?.data || onboardingsResponse.data || [];

    if (onboardings.length === 0) {
      return { success: false, message: 'No onboarding records found' };
    }

    // Get all task IDs
    const taskIds = allTasks.map((task: any) => task._id || task.id);

    // Check and fix each onboarding record
    const results = [];
    for (const onboarding of onboardings) {
      try {
        // Get current tasks for this onboarding
        const currentTasks = onboarding.tasks || [];

        // Check if all tasks are assigned
        const missingTasks = taskIds.filter((taskId: string) => !currentTasks.includes(taskId));
        const extraTasks = currentTasks.filter((taskId: string) => !taskIds.includes(taskId));

        // If there are missing or extra tasks, fix them
        if (missingTasks.length > 0 || extraTasks.length > 0) {
          // Create the correct task list (current tasks minus extra, plus missing)
          const correctedTasks = Array.from(
            new Set([...currentTasks, ...missingTasks])
          ).filter((taskId) => !extraTasks.includes(taskId));

          // Update the onboarding with the corrected task list
          await axios.put(`/onboarding/${onboarding._id}/tasks`, {
            taskIds: correctedTasks,
          });

          results.push({
            onboardingId: onboarding._id,
            success: true,
            missingAdded: missingTasks.length,
            extraRemoved: extraTasks.length
          });
        } else {
          results.push({
            onboardingId: onboarding._id,
            success: true,
            missingAdded: 0,
            extraRemoved: 0
          });
        }

      } catch (error: any) {
        results.push({
          onboardingId: onboarding._id,
          success: false,
          error: error.message
        });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    return {
      success: successCount > 0,
      message: `Fixed task assignments for ${successCount} onboarding records${failureCount > 0 ? `, ${failureCount} failed` : ''}`,
      results
    };
  } catch (error: any) {
    throw error;
  }
} 